import { RegionSetStatusType } from "@app/projects/enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import {
  AttestationAggregateResultGroup,
  AttestationRegionSetAggregate,
  AttestationSpecialtyGroupAggregate,
} from "@app/projects/results/attestation";
import {
  AnalysisSpecialtyGroup,
  ResultRegion,
} from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";

interface AttestationAggregate {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  distinctNPINumAttested: number;
  distinctNPIPercentAttested: number;
  distinctNPINumUnattested: number;
  numAttested: number;
  numUnattested: number;
  percentAttested: number;
  resultGroups: AttestationAggregateResultGroup[];

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionStatus?: RegionSetStatusType;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode?: string;
  specialtyGroupDisplayName?: string;
  specialtyGroupDisplayOrder?: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational?: boolean;
  specialtyGroupName?: string;
}

class AttestationAggregateImpl implements AttestationAggregate {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public distinctNPINumAttested: number,
    public distinctNPIPercentAttested: number,
    public distinctNPINumUnattested: number,
    public numAttested: number,
    public numUnattested: number,
    public percentAttested: number,
    public resultGroups: AttestationAggregateResultGroup[],

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public regionStatus?: RegionSetStatusType,

    public specialtyGroupCode?: string,
    public specialtyGroupDisplayName?: string,
    public specialtyGroupDisplayOrder?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupIsInformational?: boolean,
    public specialtyGroupName?: string,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${this.specialtyGroupCode}`;
      case "unknown":
        return null;
    }
  }
}

function buildAttestationAggregate(
  key: string,
  resultRegion: ResultRegion,
  aggregate: AttestationRegionSetAggregate | AttestationSpecialtyGroupAggregate,
  aggregateDisplayType: AggregateDisplayType,
  specialtyGroup?: AnalysisSpecialtyGroup,
  regionStatus?: RegionSetStatusType,
): AttestationAggregate {
  if (!resultRegion || !aggregate) {
    return {} as AttestationAggregate;
  }
  if (isSpecialtyGroupAggregate(aggregate)) {
    //A specialty group is required and only used when passing in an aggregate of this type
    //we're not checking if it (specialty group) exists bc we want it to fail if it doesn't exist
    return new AttestationAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.distinctNPINumAttested,
      aggregate.distinctNPIPercentAttested,
      aggregate.distinctNPINumUnattested,
      aggregate.numAttested,
      aggregate.numUnattested,
      aggregate.percentAttested,
      aggregate.resultGroups,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      regionStatus,

      specialtyGroup.code,
      `${specialtyGroup.code} - ${specialtyGroup.name}`,
      specialtyGroup.displayOrder,
      specialtyGroup.entityType,
      specialtyGroup.isInformational,
      specialtyGroup.name,
    );

  } else {
    return new AttestationAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.distinctNPINumAttested,
      aggregate.distinctNPIPercentAttested,
      aggregate.distinctNPINumUnattested,
      aggregate.numAttested,
      aggregate.numUnattested,
      aggregate.percentAttested,
      aggregate.resultGroups,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      regionStatus,

      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    );
  }
}

/**
 * Clones an `AttestationAggregate` while setting a new displayText property based on the `AttestationAggregateDisplayType`
 * It DOES actually create a new object
 */
function psuedoClone(
  aggregate: AttestationAggregate,
  aggregateDisplayType: AggregateDisplayType,
): AttestationAggregate {
  return new AttestationAggregateImpl(
    aggregate.key,
    aggregateDisplayType,

    aggregate.distinctNPINumAttested,
    aggregate.distinctNPIPercentAttested,
    aggregate.distinctNPINumUnattested,
    aggregate.numAttested,
    aggregate.numUnattested,
    aggregate.percentAttested,
    aggregate.resultGroups,

    aggregate.regionCode,
    aggregate.regionGranularity,
    aggregate.regionSetDisplayName,
    aggregate.regionSetId,
    aggregate.subRegionCode,

    aggregate.regionStatus,

    aggregate.specialtyGroupCode,
    aggregate.specialtyGroupDisplayName,
    aggregate.specialtyGroupDisplayOrder,
    aggregate.specialtyGroupEntityType,
    aggregate.specialtyGroupIsInformational,
    aggregate.specialtyGroupName,
  );
}

function isSpecialtyGroupAggregate(aggregate: AttestationRegionSetAggregate | AttestationSpecialtyGroupAggregate): aggregate is AttestationSpecialtyGroupAggregate {
  return (aggregate as AttestationSpecialtyGroupAggregate).specialtyGroupCode !== undefined;
}

export { AttestationAggregate, buildAttestationAggregate, psuedoClone };
